/**
 * CihReveal — subtle fade-up on mount used on CIH landing pages. Wraps a
 * block; respects reduced motion. `delay` staggers siblings (seconds).
 *
 * Deliberately mount-based, NOT scroll-triggered (`whileInView`): scroll
 * reveals leave below-fold content at opacity 0 until hydration + scroll,
 * which blanks full-page screenshots and hides content when JS is slow.
 *
 * Aliases: reveal, fade up, mount reveal
 */

import { motion } from "motion/react";
import type { ReactNode } from "react";

import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";

type Props = {
  readonly children: ReactNode;
  readonly delay?: number;
  readonly className?: string;
};

export function CihReveal({ children, delay = 0, className }: Props) {
  const reducedMotion = useReducedMotionSafe();

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 16 }}
      animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
